/**
 * Map system dashboard summary sections to DashboardPage stats.
 * section === null means unauthorized/unavailable (NOT zero).
 * attention is authoritative from the backend (R2-D).
 */

const SYSTEM_ATTENTION_BY_TYPE = {
  subscription_past_due: {
    id: 'past-due',
    titleKey: 'dashboard:attention.past_due_title',
    detailKey: 'dashboard:attention.past_due_detail',
    to: '/subscriptions',
  },
  subscription_payment_blocked: {
    id: 'blocked',
    titleKey: 'dashboard:attention.blocked_title',
    detailKey: 'dashboard:attention.blocked_detail',
    to: '/subscriptions',
  },
  payment_pending: {
    id: 'pending',
    titleKey: 'dashboard:attention.pending_title',
    detailKey: 'dashboard:attention.pending_detail',
    to: '/payments',
  },
  tenant_not_ready: {
    id: 'not-ready',
    titleKey: 'dashboard:attention.not_ready_title',
    detailKey: 'dashboard:attention.not_ready_detail',
    to: '/provisioning/readiness?overall_status=not_ready',
  },
  support_open: {
    id: 'tickets',
    titleKey: 'dashboard:attention.tickets_title',
    detailKey: 'dashboard:attention.tickets_detail',
    to: '/support',
  },
};

const PORTAL_ATTENTION_BY_TYPE = {
  payment_action_required: {
    id: 'payment',
    titleKey: 'dashboard:portal.attention_payment_title',
    detailKey: 'dashboard:payment_warning',
    to: '/portal/billing',
  },
  provisioning_issue: {
    id: 'provisioning',
    titleKey: 'dashboard:portal.attention_provisioning_title',
    detailKey: 'dashboard:provisioning_warning',
    to: '/portal/tenants',
  },
  schema_issue: {
    id: 'schema',
    titleKey: 'dashboard:portal.attention_schema_title',
    detailKey: 'dashboard:schema_warning',
    to: '/portal/tenants',
  },
  support_open: {
    id: 'tickets',
    titleKey: 'dashboard:portal.attention_tickets_title',
    detailKey: 'dashboard:portal.attention_tickets_detail',
    to: '/portal/support',
  },
};

function normalizeAttentionRows(attention) {
  return Array.isArray(attention) ? attention : [];
}

/**
 * Map backend attention rows to AttentionList items.
 * Unknown types are skipped (no crash, no raw type render).
 * Missing/non-array attention → empty list (no client business fallback).
 */
export function buildSystemAttentionItems(attention, t) {
  const items = [];
  for (const row of normalizeAttentionRows(attention)) {
    const type = row?.type;
    const mapping = SYSTEM_ATTENTION_BY_TYPE[type];
    if (!mapping) {
      continue;
    }
    const count = typeof row.count === 'number' ? row.count : 0;
    items.push({
      id: mapping.id,
      title: t(mapping.titleKey),
      detail: t(mapping.detailKey, { count }),
      to: mapping.to,
      badgeLabel: String(count),
      tone: row.severity || 'warning',
    });
  }
  return items;
}

/**
 * Map backend portal attention rows to AttentionList items.
 */
export function buildPortalAttentionItems(attention, t) {
  const items = [];
  for (const row of normalizeAttentionRows(attention)) {
    const type = row?.type;
    const mapping = PORTAL_ATTENTION_BY_TYPE[type];
    if (!mapping) {
      continue;
    }
    const count = typeof row.count === 'number' ? row.count : 0;
    items.push({
      id: mapping.id,
      title: t(mapping.titleKey),
      detail: t(mapping.detailKey, { count }),
      to: mapping.to,
      badgeLabel: String(count),
      tone: row.severity || 'warning',
    });
  }
  return items;
}

export function mapSystemDashboardSummary(summary) {
  const billing = summary?.billing ?? null;
  const tenants = summary?.tenants ?? null;
  const support = summary?.support ?? null;

  return {
    active: billing == null ? null : (billing.active_subscriptions ?? 0),
    trial: billing == null ? null : (billing.trial_subscriptions ?? 0),
    pastDue: billing == null ? null : (billing.past_due_subscriptions ?? 0),
    blocked: billing == null ? null : (billing.payment_blocked_subscriptions ?? 0),
    pendingPayments: billing == null ? null : (billing.pending_payments ?? 0),
    notReadyTenants: tenants == null || tenants.not_ready == null ? null : tenants.not_ready,
    readyTenants: tenants == null || tenants.ready == null ? null : tenants.ready,
    warningTenants: tenants == null || tenants.warning == null ? null : tenants.warning,
    provisioningFailed: tenants == null || tenants.provisioning_failed == null
      ? null
      : tenants.provisioning_failed,
    schemaFailed: tenants == null || tenants.schema_failed == null
      ? null
      : tenants.schema_failed,
    openTickets: support == null || support.open == null ? null : support.open,
    attention: normalizeAttentionRows(summary?.attention),
  };
}

function setupSummaryLabel(tenantSetup, t) {
  switch (tenantSetup) {
    case 'completed':
      return t('setupWizard:portal.setup_completed');
    case 'in_progress':
      return t('setupWizard:portal.setup_in_progress');
    case 'blocked':
      return t('setupWizard:portal.setup_blocked');
    case 'skipped':
      return t('setupWizard:portal.setup_skipped');
    case 'not_started':
    case 'pending':
    default:
      return t('setupWizard:portal.setup_not_started');
  }
}

function setupSegmentCounts(tenantSetup, tenantCount) {
  const n = typeof tenantCount === 'number' ? tenantCount : 0;
  const counts = {
    setupCompleted: 0,
    setupInProgress: 0,
    setupBlocked: 0,
    setupNotStarted: 0,
  };
  if (n <= 0) {
    return counts;
  }
  switch (tenantSetup) {
    case 'completed':
      counts.setupCompleted = n;
      break;
    case 'in_progress':
      counts.setupInProgress = n;
      break;
    case 'blocked':
      counts.setupBlocked = n;
      break;
    case 'skipped':
      counts.setupNotStarted = n;
      break;
    default:
      counts.setupNotStarted = n;
      break;
  }
  return counts;
}

/**
 * Map portal dashboard summary to CustomerPortalDashboardPage stats.
 * Null sections mean unauthorized/unavailable — do not coerce to zero.
 */
export function mapPortalDashboardSummary(summary, { emptyValue, t, getSubscriptionStatusLabel }) {
  const overview = summary?.overview ?? null;
  const readiness = summary?.readiness ?? null;
  const billing = summary?.billing ?? null;
  const support = summary?.support ?? null;
  const modules = summary?.modules ?? null;

  const subscriptionStatus = overview?.subscription_status ?? null;
  const tenantCount = overview?.tenant_count;
  const openTickets = support == null ? null : (support.open ?? 0);
  const requiresPayment = overview?.requires_payment_action === true;
  const provisioningIssue = readiness?.provisioning === 'issue';
  const schemaIssue = readiness?.schema === 'issue';
  const setupCounts = readiness == null
    ? {
      setupCompleted: 0,
      setupInProgress: 0,
      setupBlocked: 0,
      setupNotStarted: 0,
    }
    : setupSegmentCounts(readiness.tenant_setup, typeof tenantCount === 'number' ? tenantCount : 0);

  const amountDue = billing?.amount_due;
  const paymentStatus = billing?.payment_status ?? null;
  const planName = billing?.current_plan?.name ?? null;

  return {
    companyId: summary?.company?.id ?? null,
    companyName: summary?.company?.name ?? null,
    tenants: overview == null || tenantCount == null ? emptyValue : tenantCount,
    tenantsRaw: typeof tenantCount === 'number' ? tenantCount : null,
    openTickets: openTickets == null ? emptyValue : openTickets,
    openTicketsRaw: openTickets,
    subscription: subscriptionStatus
      ? (getSubscriptionStatusLabel(subscriptionStatus) || subscriptionStatus)
      : emptyValue,
    subscriptionRaw: subscriptionStatus || '',
    paymentIssues: overview == null ? 0 : (requiresPayment ? 1 : 0),
    paymentIssuesAvailable: overview != null && overview.requires_payment_action != null,
    setupSummary: readiness == null
      ? emptyValue
      : setupSummaryLabel(readiness.tenant_setup, t),
    ...setupCounts,
    readinessAvailable: readiness != null,
    provisioningIssues: readiness == null ? 0 : (provisioningIssue ? 1 : 0),
    schemaIssues: readiness == null ? 0 : (schemaIssue ? 1 : 0),
    planName: billing == null ? emptyValue : (planName || emptyValue),
    amountDue: billing == null
      ? emptyValue
      : (amountDue != null ? `${amountDue} EUR` : emptyValue),
    paymentStatus: billing == null ? emptyValue : (paymentStatus || emptyValue),
    paidUntil: billing == null ? null : (billing.paid_until ?? null),
    graceUntil: billing == null ? null : (billing.grace_until ?? null),
    billingAvailable: billing != null,
    supportAvailable: support != null,
    latestTicket: support?.latest_ticket ?? null,
    modulesCount: emptyValue,
    modulesActiveCount: modules?.active_count ?? null,
    attention: normalizeAttentionRows(summary?.attention),
  };
}
